<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { message } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import { useRouter } from 'vue-router';
import DataTable from '@/components/common/DataTable.vue';
import { getPointLogs, getPointTypeOptions } from '@/api/points';
import formatDateTime from '@/utils/formatDateTime';
import dayjs from 'dayjs';
import { searchUsers } from '@/api/users';
import { useAdminInfo } from '@/stores/useAdminInfo';

const router = useRouter();
const adminStore = useAdminInfo();

// 搜尋表單狀態
const searchForm = reactive({
    user_id: undefined,
    point_type: '',
    created_range: []
});

// 會員搜尋相關
const userOptions = ref([]);
const userLoading = ref(false);
let userSearchTimer = null;
const USER_SEARCH_DEBOUNCE_MS = 250;

const fetchUserOptions = async (keyword) => {
    if (!keyword || keyword.trim().length < 2) {
        userOptions.value = [];
        return;
    }
    try {
        userLoading.value = true;
        const res = await searchUsers({ name: keyword.trim(), limit: 10 });
        if (res?.result) {
            const arr = Array.isArray(res.data)
                ? res.data
                : res.data?.data || [];
            userOptions.value = arr.map((u) => {
                const name = u.name || u.user_name || `#${u.id}`;
                const mail = u.mail || u.email || '';
                const label = mail ? `${name}(${mail})` : name;
                return {
                    label,
                    value: u.id
                };
            });
        }
    } catch {
    } finally {
        userLoading.value = false;
    }
};

// 查看會員詳情
const handleViewMember = (userId) => {
    if (!userId) return;
    if (!adminStore.hasPermissionCode('100110')) {
        // 會員詳情
        message.warning('您沒有查看會員詳情的權限');
        return;
    }
    router.push(`/members/${userId}`);
};

const onUserSearch = (val) => {
    clearTimeout(userSearchTimer);
    userSearchTimer = setTimeout(
        () => fetchUserOptions(val),
        USER_SEARCH_DEBOUNCE_MS
    );
};

// 這些回調函數會在使用前定義完整

const pointTypeOptions = ref([{ label: '全部', value: '' }]);

// 表格狀態
const tableData = ref([]);
const loading = ref(false);
const pagination = reactive({ total: 0, current: 1, pageSize: 20 });
const lastQueryRef = ref({});

// 欄位定義
const columns = [
    // 流水號改由 DataTable 內建序號列提供，這裡不再顯示 ID 與會員ID
    {
        title: '會員',
        key: 'user',
        width: 220,
        slots: { customRender: 'user' }
    },
    {
        title: '類型',
        dataIndex: 'point_type',
        key: 'point_type',
        width: 120,
        slots: { customRender: 'point_type' }
    },
    {
        title: '變動數值',
        dataIndex: 'change',
        key: 'change',
        width: 120,
        align: 'left',
        slots: { customRender: 'change' }
    },
    {
        title: '變動前',
        dataIndex: 'before',
        key: 'before',
        width: 120,
        align: 'left'
    },
    {
        title: '變動後',
        dataIndex: 'after',
        key: 'after',
        width: 120,
        align: 'left'
    },
    { title: '原因', dataIndex: 'reason', key: 'reason', ellipsis: true },
    {
        title: '操作者',
        key: 'reviewer_name',
        width: 120,
        slots: { customRender: 'reviewer_name' }
    },
    {
        title: '建立時間',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 180,
        slots: { customRender: 'created_at' }
    }
];

// 查詢函式（集中處理 API 錯誤）
const fetchLogs = async (extraParams = {}) => {
    try {
        loading.value = true;
        const params = {
            page: pagination.current,
            per_page: pagination.pageSize,
            ...lastQueryRef.value,
            ...extraParams
        };
        const res = await getPointLogs(params);
        if (res?.result || res?.success) {
            // 兼容不同返回格式
            const list = res.data?.data || res.data?.list || [];
            const apiPagination = res.data?.pagination || {};
            const total = Number(apiPagination.total ?? res.data?.total ?? 0);
            const current = Number(
                apiPagination.current_page ??
                    apiPagination.current ??
                    pagination.current
            );
            const perPage = Number(
                apiPagination.per_page ??
                    apiPagination.pageSize ??
                    pagination.pageSize
            );
            tableData.value = list;
            pagination.total = total;
            pagination.current = current;
            pagination.pageSize = perPage;
            return res;
        } else {
            showErrorMessage(res?.message || '取得積分紀錄失敗');
            tableData.value = [];
            pagination.total = 0;
            return res;
        }
    } catch (err) {
        console.error('GET /api/admin/logs/point 失敗', err);
        tableData.value = [];
        pagination.total = 0;
        return null;
    } finally {
        loading.value = false;
    }
};

// 轉換搜尋表單為 API 參數
const normalizeSearchParams = (params = {}) => {
    const p = { ...params };
    if (Array.isArray(p.created_range) && p.created_range.length === 2) {
        const [start, end] = p.created_range;
        p.created_from = start ? dayjs(start).format('YYYY-MM-DD') : undefined;
        p.created_to = end ? dayjs(end).format('YYYY-MM-DD') : undefined;
    }
    delete p.created_range;
    return Object.fromEntries(
        Object.entries(p).filter(
            ([, v]) => v !== '' && v !== undefined && v !== null
        )
    );
};

const handleSearch = async () => {
    pagination.current = 1;
    lastQueryRef.value = normalizeSearchParams(searchForm);
    await fetchLogs();
};

const handleReset = async () => {
    searchForm.user_id = undefined;
    searchForm.point_type = '';
    searchForm.created_range = [];
    pagination.current = 1;
    lastQueryRef.value = {};
    await fetchLogs();
};

// 現在定義回調函數
const onUserChange = (val) => {
    searchForm.user_id = val;
    handleSearch();
};

const onUserClear = () => {
    searchForm.user_id = undefined;
    handleSearch();
};

const onPointTypeChange = (val) => {
    searchForm.point_type = val;
    handleSearch();
};

const onDateRangeChange = (val) => {
    searchForm.created_range = val;
    handleSearch();
};

// 更新 searchItems 的回調 - 在 onMounted 中執行，確保 SearchCard 已初始化
onMounted(async () => {
    // 初始化積分類型選項
    try {
        const res = await getPointTypeOptions();
        if (res?.result || res?.success) {
            const data = res.data || {};
            pointTypeOptions.value = [
                { label: '全部', value: '' },
                ...Object.keys(data).map((k) => ({ label: k, value: k }))
            ];
        }
    } catch {}

    await fetchLogs();
});

// 表格換頁/排序（兼容 DataTable 可能傳入的不同事件形狀）
const handleTableChange = async (...args) => {
    const first = args?.[0] || {};
    const p = first?.pagination || first;
    if (p?.current) pagination.current = p.current;
    if (p?.pageSize) pagination.pageSize = p.pageSize;
    await fetchLogs();
};

// 導出給模板使用
const tableColumns = computed(() => columns);

// 類型邊框顏色對應 - 稍深的莫蘭迪色系邊框
const pointTypeBorderColor = (t) => {
    const map = {
        CP: '#B8A4D0', // 稍深灰紫邊框
        PP: '#9DB2D1', // 稍深灰藍邊框
        EP: '#D5A6B0', // 稍深灰粉邊框
        XP: '#DDB4A5', // 稍深灰橘邊框
        TP: '#C0C0C0', // 稍深灰邊框
        GP: '#B5C9B7', // 稍深灰綠邊框
        DIAMOND: '#B8B5D3', // 稍深灰靛邊框
        GOLD: '#D8C4A9' // 稍深灰金邊框
    };
    return map[t] || '#C0C0C0';
};

// 類型顏色對應 - 莫蘭迪色系背景
const pointTypeColor = (t) => {
    const map = {
        CP: '#E8DEF8', // 淡灰紫背景
        PP: '#D6E4F0', // 淡灰藍背景
        EP: '#F4E6E8', // 淡灰粉背景
        XP: '#F5E6D6', // 淡灰橘背景
        TP: '#E8E8E8', // 淡灰背景
        GP: '#E4F1E5', // 淡灰綠背景
        DIAMOND: '#E6E4F2', // 淡灰靛背景
        GOLD: '#F5EBD8' // 淡灰金背景
    };
    return map[t] || '#E8E8E8';
};

const sourceTypeLabel = (s) => {
    if (!s) return '-';
    const key = String(s).toLowerCase();
    const map = {
        manual: '人工',
        system: '系統',
        order: '訂單',
        mission: '任務',
        task: '任務',
        refund: '退款',
        penalty: '懲罰'
    };
    return map[key] || String(s).replace(/_/g, ' ');
};
</script>

<template>
    <div class="page points-page">
        <div class="page-header">
            <h1>積分紀錄</h1>
            <p>查看與追蹤會員的積分異動紀錄</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 工具欄 -->
            <div class="toolbar">
                <div class="search-box filters-wrap">
                    <a-select
                        v-model:value="searchForm.user_id"
                        show-search
                        :filter-option="false"
                        :options="userOptions"
                        :loading="userLoading"
                        allow-clear
                        placeholder="搜尋會員"
                        size="middle"
                        class="status-select min-w-[300px]"
                        @search="onUserSearch"
                        @change="onUserChange"
                        @clear="onUserClear"
                    />
                    <a-select
                        v-model:value="searchForm.point_type"
                        :options="pointTypeOptions"
                        allow-clear
                        placeholder="積分類型"
                        size="middle"
                        class="status-select min-w-[160px]"
                        @change="onPointTypeChange"
                    />
                    <a-range-picker
                        v-model:value="searchForm.created_range"
                        :placeholder="['開始日期', '結束日期']"
                        class=""
                        @change="onDateRangeChange"
                    />
                </div>
                <a-space class="toolbar-actions">
                    <a-button
                        size="middle"
                        class="unified-search-btn"
                        @click="handleSearch"
                    >
                        查詢
                    </a-button>
                    <a-button
                        size="middle"
                        class="unified-reset-btn"
                        @click="handleReset"
                    >
                        重置
                    </a-button>
                </a-space>
            </div>

            <!-- 積分紀錄表格 -->
            <DataTable
                title=""
                :columns="tableColumns"
                :data-source="tableData"
                :pagination="pagination"
                :show-pagination="true"
                :scroll="{ x: 1400 }"
                :loading="loading"
                @change="handleTableChange"
                class="points-table left-align-table"
                :no-card="true"
            >
                <template #user="{ record }">
                    <div class="user-cell-content">
                        <div class="user-info-row">
                            <a
                                v-if="record.user_id"
                                class="user-link"
                                @click.stop="handleViewMember(record.user_id)"
                            >
                                {{ record.user_name || '-' }}
                            </a>
                            <span v-else class="user-name">
                                {{ record.user_name || '-' }}
                            </span>
                            <span
                                :class="[
                                    'source-chip',
                                    `source-chip-${record.source_type}`
                                ]"
                            >
                                {{ sourceTypeLabel(record.source_type) }}
                            </span>
                        </div>
                    </div>
                </template>
                <template #change="{ text }">
                    <span
                        :class="
                            Number(text) >= 0
                                ? 'text-green-600 font-semibold'
                                : 'text-red-500 font-semibold'
                        "
                    >
                        {{ Number(text) >= 0 ? '+' : '' }}{{ text }}
                    </span>
                </template>
                <template #point_type="{ text }">
                    <span
                        class="type-tag-custom"
                        :style="{
                            backgroundColor: pointTypeColor(text),
                            borderColor: pointTypeBorderColor(text)
                        }"
                    >
                        {{ text }}
                    </span>
                </template>
                <template #reviewer_name="{ record }">
                    {{ record.reviewer_name || '-' }}
                </template>
                <template #created_at="{ text }">
                    {{ text ? formatDateTime(text) : '-' }}
                </template>
            </DataTable>
        </div>
    </div>
</template>

<style scoped>
.points-page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header {
    margin-bottom: 24px;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.page-header p {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

/* 工具欄樣式 - 對齊會員積分頁 */
.toolbar {
    margin-bottom: 16px;
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    border: 1px solid rgba(200, 200, 210, 0.2);
    flex-wrap: wrap;
}

.search-box {
    display: flex;
    align-items: center;
    gap: 16px;
}

/* 下拉選單樣式 - 與會員管理/角色管理一致 */
.search-box :deep(.status-select .ant-select),
.toolbar :deep(.status-select .ant-select) {
    border-radius: 8px !important;
}

.search-box :deep(.status-select .ant-select-selector),
.toolbar :deep(.status-select .ant-select-selector) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.1) !important;
}

.search-box :deep(.status-select .ant-select-selector:hover),
.toolbar :deep(.status-select .ant-select-selector:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.2) !important;
}

.search-box :deep(.status-select .ant-select-focused .ant-select-selector),
.toolbar :deep(.status-select .ant-select-focused .ant-select-selector) {
    border-color: #2a4a7a !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
}

.search-box :deep(.status-select .ant-select-selection-item),
.toolbar :deep(.status-select .ant-select-selection-item) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

.search-box :deep(.status-select .ant-select-selection-placeholder),
.toolbar :deep(.status-select .ant-select-selection-placeholder) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: rgba(42, 42, 58, 0.5) !important;
    font-family: 'Courier New', monospace !important;
}

.search-box :deep(.status-select .ant-select-arrow),
.toolbar :deep(.status-select .ant-select-arrow) {
    color: rgba(42, 42, 58, 0.6) !important;
    transition: color 0.3s ease !important;
}

.search-box :deep(.status-select .ant-select:hover .ant-select-arrow),
.toolbar :deep(.status-select .ant-select:hover .ant-select-arrow) {
    color: #2a4a7a !important;
}

/* 下拉選單下拉框樣式 */
.search-box :deep(.status-select .ant-select-dropdown),
.toolbar :deep(.status-select .ant-select-dropdown) {
    background: rgba(255, 255, 255, 0.98) !important;
    backdrop-filter: blur(10px) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 12px !important;
    box-shadow: 0 0 25px rgba(42, 74, 122, 0.25) !important;
    padding: 8px !important;
    margin-top: 4px !important;
}

.search-box :deep(.status-select .ant-select-item),
.toolbar :deep(.status-select .ant-select-item) {
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    transition: all 0.2s ease !important;
}

.search-box :deep(.status-select .ant-select-item:hover),
.toolbar :deep(.status-select .ant-select-item:hover) {
    background: rgba(42, 74, 122, 0.15) !important;
    color: #2a4a7a !important;
}

.search-box :deep(.status-select .ant-select-item-option-selected),
.toolbar :deep(.status-select .ant-select-item-option-selected) {
    background: rgba(42, 74, 122, 0.25) !important;
    color: #2a4a7a !important;
    font-weight: 600 !important;
}

.search-box :deep(.status-select .ant-select-item-option-active),
.toolbar :deep(.status-select .ant-select-item-option-active) {
    background: rgba(42, 74, 122, 0.15) !important;
}

/* 日期選擇器樣式 - 與下拉選單一致 */
.search-box :deep(.ant-picker),
.toolbar :deep(.ant-picker) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.1) !important;
}

.search-box :deep(.ant-picker:hover),
.toolbar :deep(.ant-picker:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.2) !important;
}

.search-box :deep(.ant-picker-focused),
.toolbar :deep(.ant-picker-focused) {
    border-color: #2a4a7a !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
}

/* 按鈕樣式 - 使用統一的 unified 類別 */

/* 以會員積分頁相同的卡片外觀包住內容 */
.content {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid transparent;
    border-radius: 16px;
    padding: 32px;
    position: relative;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
}

/* DataTable 外層卡片對齊其他頁 */
::deep(.ant-card) {
    background: transparent !important;
    border: none !important;
}

::deep(.ant-card-body) {
    padding: 0 !important;
}

.user-name {
    color: #2a2a3a;
    font-weight: 600;
}

.user-link {
    color: #2a4a7a;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s ease;
    font-family: 'Courier New', monospace;
}

.user-link:hover {
    color: #2a4a7a;
    text-decoration: underline;
    text-shadow: 0 0 8px rgba(42, 74, 122, 0.3);
}

/* 會員欄位樣式 */
.user-cell-content {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.user-info-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

/* 來源標籤（人工/系統） - 更精緻的樣式 */
.source-chip {
    display: inline-block;
    border-radius: 6px !important;
    padding: 2px 8px !important;
    height: 20px !important;
    line-height: 16px !important;
    font-size: 11px !important;
    background: rgba(255, 255, 255, 0.9) !important;
    color: #2a2a3a !important;
    font-weight: 600 !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    font-family: 'Courier New', monospace !important;
}

/* 人工 - 莫蘭迪深黃 */
.source-chip-manual {
    border-color: #b8a05a !important;
    color: #b8a05a !important;
    background: rgba(184, 160, 90, 0.1) !important;
}

/* 系統 - 莫蘭迪深藍 */
.source-chip-system {
    border-color: #5b7a8a !important;
    color: #5b7a8a !important;
    background: rgba(91, 122, 138, 0.1) !important;
}

/* 訂單 - 深藍 */
.source-chip-order {
    border-color: #5b7a8a !important;
    color: #5b7a8a !important;
    background: rgba(91, 122, 138, 0.1) !important;
}

/* 任務 - 莫蘭迪深藍 */
.source-chip-mission,
.source-chip-task {
    border-color: #5b7a8a !important;
    color: #5b7a8a !important;
    background: rgba(91, 122, 138, 0.1) !important;
}

/* 退款 - 莫蘭迪深紅 */
.source-chip-refund {
    border-color: #a67a7a !important;
    color: #a67a7a !important;
    background: rgba(166, 122, 122, 0.1) !important;
}

/* 懲罰 - 紅色 */
.source-chip-penalty {
    border-color: #a67a7a !important;
    color: #a67a7a !important;
    background: rgba(166, 122, 122, 0.1) !important;
}

/* 積分類型標籤（PP/CP/EP/XP）- 莫蘭迪色系，背景淡色 + 邊框有明顯落差 */
.type-tag-custom {
    display: inline-block;
    border-radius: 8px !important;
    padding: 4px 12px !important;
    height: 24px !important;
    line-height: 16px !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    border: 1.5px solid;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

/* 積分類型下拉與選中值文字靠左 */
.left-text-select :deep(.ant-select-selection-item),
.left-text-select :deep(.ant-select-selection-placeholder) {
    text-align: left !important;
    justify-content: flex-start !important;
}

.left-text-select :deep(.ant-select-item-option-content) {
    text-align: left !important;
}

/* 表格資料列左對齊（空狀態列除外，由 DataTable 置中） */
.left-align-table :deep(.ant-table .ant-table-thead > tr > th),
.left-align-table
    :deep(.ant-table .ant-table-tbody > tr:not(.ant-table-placeholder) > td) {
    text-align: left !important;
}

/* 強化 Select 內外層全部靠左 */
.left-text-select :deep(.ant-select-selector) {
    text-align: left !important;
}

.left-text-select :deep(.ant-select-item) {
    text-align: left !important;
}

/* 搜尋列可換行並左對齊排版 */
.filters-wrap {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
}

/* 將查詢/重置固定靠右 */
.toolbar-actions {
    margin-left: auto;
}
</style>
